// Energy unit conversion utilities. Mirrors backend/utils/energy_units.py.
// Keep both files in sync.

// 1 unit = X joules
export const ENERGY_UNIT_TO_JOULES = {
  J: 1,
  kJ: 1e3,
  MJ: 1e6,
  GJ: 1e9,
  TJ: 1e12,
  Wh: 3600,
  kWh: 3.6e6,
  MWh: 3.6e9,
  GWh: 3.6e12,
  kcal: 4184,
  Mcal: 4.184e6,
  Gcal: 4.184e9,
  BTU: 1055.05585262,
  MMBtu: 1.05505585262e9,
  therm: 1.0548e8,
};

export const DISPLAY_UNITS = ['J', 'kJ', 'MJ', 'GJ', 'kWh', 'MWh', 'kcal', 'Mcal', 'Gcal', 'MMBtu'];

// Net calorific values used for fuel-quantity → energy conversion.
// Aligned with backend/emission_factors.py — keep in sync.
// Each entry: NCV (MJ/native_unit) and the native unit it applies to.
export const FUEL_NCV = {
  Diesel:  { ncv: 43.0, unit: 'L'   },
  FO:      { ncv: 40.2, unit: 'L'   },
  PNG:     { ncv: 38.0, unit: 'SCM' },
  LPG:     { ncv: 46.1, unit: 'kg'  },
  Coal:    { ncv: 18.5, unit: 'kg'  },
  Biomass: { ncv: 15.0, unit: 'kg'  },
};

export function convertEnergy(value, fromUnit, toUnit) {
  const fromFactor = ENERGY_UNIT_TO_JOULES[fromUnit];
  const toFactor = ENERGY_UNIT_TO_JOULES[toUnit];
  if (fromFactor == null || toFactor == null) return null;
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  return (v * fromFactor) / toFactor;
}

export function convertToAll(value, fromUnit) {
  const out = {};
  for (const u of DISPLAY_UNITS) {
    out[u] = convertEnergy(value, fromUnit, u);
  }
  return out;
}

// Same-domain unit aliases for fuels. SCM is intentionally absent (gas at
// standard conditions; not interchangeable with m³/L without molar volume).
// Mass base = kg, Volume base = L.
const FUEL_UNIT_TO_BASE = {
  // mass → kg
  mg:    { domain: 'mass',   factor: 0.000001 },
  g:     { domain: 'mass',   factor: 0.001 },
  kg:    { domain: 'mass',   factor: 1.0 },
  t:     { domain: 'mass',   factor: 1000.0 },
  mt:    { domain: 'mass',   factor: 1000.0 },
  ton:   { domain: 'mass',   factor: 1000.0 },
  tonne: { domain: 'mass',   factor: 1000.0 },
  // volume → L
  ml:    { domain: 'volume', factor: 0.001 },
  l:     { domain: 'volume', factor: 1.0 },
  liter: { domain: 'volume', factor: 1.0 },
  litre: { domain: 'volume', factor: 1.0 },
  kl:    { domain: 'volume', factor: 1000.0 },
  m3:    { domain: 'volume', factor: 1000.0 },
  'm³':  { domain: 'volume', factor: 1000.0 },
};

function classifyFuelUnit(unit) {
  if (!unit) return null;
  return FUEL_UNIT_TO_BASE[String(unit).trim().toLowerCase()] || null;
}

// Convert fuel quantity (L / kg / SCM / MT / m³ etc.) → MJ. Returns null
// when the unit can't be safely converted to the fuel's native master unit
// (e.g. MT of Diesel — mass↔volume needs density). Same-domain conversion
// (MT→kg, L→m³ etc.) is automatic.
export function fuelQuantityToMJ(quantity, fuelType, fuelUnit, ncvOverride) {
  const master = FUEL_NCV[fuelType];
  const ncv = ncvOverride != null ? ncvOverride : master ? master.ncv : null;
  if (ncv == null) return null;
  const q = Number(quantity);
  if (!Number.isFinite(q)) return null;

  const nativeUnit = master ? master.unit : null;

  // Fast paths.
  if (!nativeUnit || !fuelUnit || nativeUnit === fuelUnit) return q * ncv;

  // Same-domain conversion.
  const row = classifyFuelUnit(fuelUnit);
  const m = classifyFuelUnit(nativeUnit);
  if (row && m && row.domain === m.domain) {
    return (q * row.factor / m.factor) * ncv;
  }

  // Cross-domain or gas-specific — refuse to guess.
  return null;
}

// Pretty-format a converted value with adaptive precision.
export function formatEnergyValue(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs === 0) return '0';
  if (abs >= 1e9) return value.toExponential(3);
  if (abs >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (abs >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  if (abs >= 0.001) return value.toLocaleString(undefined, { maximumFractionDigits: 5 });
  return value.toExponential(3);
}
